import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, User, Edit, CheckCircleIcon } from 'lucide-react';
import type { QuoteWizardData } from '../QuoteWizard';
import type { WizardStepProps } from '../../../framework/wizard';
import { farmerService } from '../../../services/farmerService';
import { quoteService } from '../../../services/quoteService';
import type { Farmer } from '../../../types/farmer';
import type { QuotationAggregates } from '../../../types/quote';
import { formatCurrency as formatPricingCurrency } from '../utils/pricingCalculator';

export const PaymentDetailsStepNew: React.FC<WizardStepProps<QuoteWizardData>> = ({ 
  data, 
  isValid, 
  errors 
}) => {
  // Check if quote is approved (read-only mode)
  const isReadOnly = data.quote?.state === 'approved';
  const navigate = useNavigate();
  const [farmers, setFarmers] = useState<Record<string, Farmer>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aggregates, setAggregates] = useState<QuotationAggregates | null>(null);

  // Load farmer details for all participants
  useEffect(() => {
    const loadFarmerDetails = async () => {
      if (!data.quote?.participants?.length) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const farmerPromises = data.quote.participants
          .filter(p => p.farmerId) // Only load farmers, not organizations
          .map(async (participant) => {
            if (participant.farmerId) {
              const farmer = await farmerService.getFarmerById(participant.farmerId);
              return { farmerId: participant.farmerId, farmer: farmer.farmer };
            }
            return null;
          });

        const farmerResults = await Promise.all(farmerPromises);
        const farmersMap: Record<string, Farmer> = {};
        
        farmerResults.forEach(result => {
          if (result) {
            farmersMap[result.farmerId] = result.farmer;
          }
        });

        setFarmers(farmersMap);
      } catch (err) {
        console.error('Error loading farmer details:', err);
        setError('Failed to load farmer information');
      } finally {
        setLoading(false);
      }
    };

    loadFarmerDetails();
  }, [data.quote?.participants]);

  // Load quotation aggregates for premium display
  useEffect(() => {
    const loadAggregates = async () => {
      if (!data.quote?.id) {
        return;
      }

      // First, check if aggregates are already stored in quotationResults
      const storedAggregates = (data.quote?.quotationResults as { aggregates?: QuotationAggregates })?.aggregates;
      if (storedAggregates && storedAggregates.total && storedAggregates.total.total_payable > 0) {
        setAggregates(storedAggregates);
        return;
      }

      try {
        // Pass protection levels to backend so it can calculate aggregates correctly
        const protectionLevels = data.quote?.protectionLevels;
        const response = await quoteService.getQuotationSummary(data.quote.id, protectionLevels);
        
        // The API client returns the data directly (unwrapped)
        const aggregatesData = response?.quotationResults?.aggregates;
        
        if (aggregatesData && aggregatesData.total) {
          setAggregates(aggregatesData);
        } else {
          setAggregates(null);
        }
      } catch (error) {
        // Graceful fallback when endpoint is not available or authentication fails
        console.error('Error loading quotation aggregates:', error);
        setAggregates(null);
      }
    };

    loadAggregates();
  }, [data.quote?.id, data.quote?.protectionLevels, data.quote?.quotationResults]);

  const handleEditPaymentDetails = (farmerId: string) => {
    // Navigate to farmer edit page with wizard context
    const currentPath = window.location.pathname;
    const returnUrl = `${currentPath}?step=6`; // Return to payment details step
    navigate(`/admin/farmers/${farmerId}/edit?returnTo=${encodeURIComponent(returnUrl)}&context=wizard`);
  };

  type ExtractedPayment = {
    type: string;
    provider?: string;
    number?: string;
    bankName?: string;
    accountNumber?: string;
    branchCode?: string;
  };

  const extractPaymentInfo = (farmer: Farmer): ExtractedPayment | null => {
    const paymentDetails = farmer.paymentDetails;
    if (!paymentDetails) return null;

    const unwrapped = typeof paymentDetails === 'object' && 'data' in paymentDetails
      ? (paymentDetails as Record<string, unknown>).data
      : paymentDetails;

    const firstPayment = Array.isArray(unwrapped) && unwrapped.length > 0
      ? (unwrapped[0] as Record<string, unknown>)
      : (typeof unwrapped === 'object' && unwrapped !== null && !Array.isArray(unwrapped))
        ? (unwrapped as Record<string, unknown>)
        : null;

    if (!firstPayment) return null;

    const paymentType = (firstPayment.type as string) || '';
    if (paymentType === 'mobile_money') {
      return {
        type: 'mobile_money',
        provider: (firstPayment.provider as string) || '',
        number: (firstPayment.registered_mobile_number as string) || (firstPayment.mobileNumber as string) || '',
      };
    }
    if (paymentType === 'bank_transfer') {
      return {
        type: 'bank_transfer',
        bankName: (firstPayment.bank_name as string) || (firstPayment.bankName as string) || '',
        accountNumber: (firstPayment.account_number as string) || (firstPayment.accountNumber as string) || '',
        branchCode: (firstPayment.branch_code as string) || (firstPayment.branchCode as string) || '',
      };
    }
    return { type: paymentType || 'unknown' };
  };

  const detectPaymentTypes = (): { hasMobile: boolean; hasBank: boolean } => {
    let hasMobile = false;
    let hasBank = false;
    for (const p of farmerParticipants) {
      const farmer = p.farmerId ? farmers[p.farmerId] : null;
      if (!farmer) continue;
      const info = extractPaymentInfo(farmer);
      if (info?.type === 'mobile_money') hasMobile = true;
      if (info?.type === 'bank_transfer') hasBank = true;
    }
    return { hasMobile, hasBank };
  };

  const participants = data.quote?.participants || [];
  const farmerParticipants = participants.filter(p => p.farmerId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading payment details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Payment Details</h3>
            <p className="mt-2 text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Payment Details</h4>
        </div>
        {isReadOnly && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Approved</span>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-600">
        Review and update payment information for all insureds. Click "Edit" to modify payment details for each farmer.
      </div>

      {/* Payment Details Table */}
      {farmerParticipants.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No farmer participants found for payment details.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            {(() => {
              const { hasMobile, hasBank } = detectPaymentTypes();
              return (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Farmer
                  </th>
                  {hasMobile && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                    </>
                  )}
                  {hasBank && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Number</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch Code</th>
                    </>
                  )}
                  {!hasMobile && !hasBank && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Info</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sum Insured
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hectares/Animals
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Premium
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {farmerParticipants.map((participant) => {
                  const farmer = participant.farmerId ? farmers[participant.farmerId] : null;
                  const paymentInfo = farmer ? extractPaymentInfo(farmer) : null;
                  
                  return (
                    <tr key={participant.farmerId} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {farmer?.name || 'Loading...'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {farmer?.phone || 'No phone'}
                            </div>
                          </div>
                        </div>
                      </td>
                      {hasMobile && (
                        <>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {paymentInfo?.type === 'mobile_money' ? paymentInfo.provider || '-' : '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {paymentInfo?.type === 'mobile_money' ? paymentInfo.number || '-' : '-'}
                          </td>
                        </>
                      )}
                      {hasBank && (
                        <>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {paymentInfo?.type === 'bank_transfer' ? paymentInfo.bankName || '-' : '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {paymentInfo?.type === 'bank_transfer' ? paymentInfo.accountNumber || '-' : '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {paymentInfo?.type === 'bank_transfer' ? paymentInfo.branchCode || '-' : '-'}
                          </td>
                        </>
                      )}
                      {!hasMobile && !hasBank && (
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {farmer ? 'No payment details' : 'Loading...'}
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {participant.sumInsured ? `$${parseFloat(String(participant.sumInsured)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {participant.hectaresAnimalCount ? participant.hectaresAnimalCount.toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          if (!participant.farmerId || !data.quote) return 'N/A';
                          
                          // Priority 1: Use aggregates (matches page 5's Total Payable)
                          // Only use if aggregates have valid data (non-zero total_payable and participants)
                          if (aggregates && aggregates.total && aggregates.total.total_payable > 0) {
                            const farmerAggregate = aggregates.participants.find(p => p.farmerId === participant.farmerId);
                            if (farmerAggregate && farmerAggregate.total_payable > 0) {
                              return formatPricingCurrency(farmerAggregate.total_payable);
                            }
                            
                            // If aggregates exist but no participant match, calculate proportionally from total
                            if (participant.sumInsured && data.quote.sumInsured && Number(data.quote.sumInsured) > 0) {
                              const premiumCents = Math.round(
                                (Number(participant.sumInsured) / Number(data.quote.sumInsured)) * aggregates.total.total_payable * 100
                              );
                              if (premiumCents > 0) {
                                return formatPricingCurrency(premiumCents / 100);
                              }
                            }
                          }
                          
                          // Priority 2: Use pricingCalculation.totalPremium if available (from page 5)
                          // This is set when aggregates are loaded on page 5, so it should have the correct total_payable
                          if (data.pricingCalculation?.totalPremium && data.pricingCalculation.totalPremium > 0) {
                            if (participant.sumInsured && data.quote.sumInsured && Number(data.quote.sumInsured) > 0) {
                              const premiumCents = Math.round(
                                (Number(participant.sumInsured) / Number(data.quote.sumInsured)) * data.pricingCalculation.totalPremium * 100
                              );
                              if (premiumCents > 0) {
                                return formatPricingCurrency(premiumCents / 100);
                              }
                            }
                            // If no sumInsured, but we have totalPremium, show the total (for single participant quotes)
                            if (!participant.sumInsured && data.quote.participants?.length === 1) {
                              return formatPricingCurrency(data.pricingCalculation.totalPremium);
                            }
                          }
                          
                          // Priority 3: Calculate proportionally based on sumInsured using quote premiumAmount (fallback)
                          if (participant.sumInsured && data.quote.sumInsured && data.quote.premiumAmount && Number(data.quote.sumInsured) > 0) {
                            const premiumCents = Math.round(
                              (Number(participant.sumInsured) / Number(data.quote.sumInsured)) * Number(data.quote.premiumAmount) * 100
                            );
                            if (premiumCents > 0) {
                            return formatPricingCurrency(premiumCents / 100);
                          }
                          }
                          return 'N/A';
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => participant.farmerId && handleEditPaymentDetails(participant.farmerId)}
                          className={`inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md ${
                            isReadOnly 
                              ? 'text-gray-500 bg-gray-100 cursor-not-allowed' 
                              : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                          disabled={!participant.farmerId || isReadOnly}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          {isReadOnly ? 'View' : 'Edit'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
              );
            })()}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Payment Details Summary</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                {farmerParticipants.length} farmer{farmerParticipants.length !== 1 ? 's' : ''} configured for payment.
                Ensure all payment details are accurate before proceeding to approval.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Errors */}
      {!isValid && errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Please resolve the following issues:</h3>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
